<template>
	<div>
		<v-row   v-if="currentState === state.Configuring">
			<v-col class="mb-3" cols="12">
				<v-alert
					:value="apiOK && !checkingApi"
					color="success"
					icon="far fa-check-circle"
					outlined
				>
					API Online: {{apiPing}}
				</v-alert>
				<v-alert
					:value="!apiOK && !checkingApi"
					color="error"
					icon="far fa-exclamation-circle"
					outlined
				>
					Could not connect to API
				</v-alert>
			</v-col>
			<v-col cols="12">
				<v-row  >
					<v-col cols="4">
						<v-select persistent-hint label="Application" v-model="token" :items="config.lists.applications" item-text="name" item-value="token"
							v-validate="'required'" data-vv-name="application" :error-messages="errors.collect('application')" required
							:append-outer-icon="tokenIcon" :class="tokenClass"
						></v-select>
					</v-col><v-col cols="8">
					</v-col>
					<v-col cols="4">
						<v-select persistent-hint v-model="analysis.type" label="Type" :items="config.enums.requestTypes" item-text="key" item-value="key"
							v-validate="'required'" data-vv-name="type" :error-messages="errors.collect('type')" required></v-select>
					</v-col>
					<v-col cols="8"></v-col>
					<v-col cols="4">
						<v-select persistent-hint v-model="analysis.format" label="Format" :items="config.enums.requestFormats" item-text="key" item-value="key"
							v-validate="'required'" data-vv-name="format" :error-messages="errors.collect('format')" required></v-select>
					</v-col>
					<v-col cols="8"></v-col>
					<v-col cols="4">
						<v-radio-group v-model="mode" row>
							<template v-slot:label>
								<div>Number of Requests</div>
							</template>
							<v-radio label="Single" value="single"></v-radio>
							<v-radio label="Multiple" value="multiple"></v-radio>
						</v-radio-group>
					</v-col>
					<v-col cols="8">
						<v-row v-if="mode === 'multiple'">
							<v-col class="pr-3" cols="3" >
								<v-text-field key="tm1" label="Number of Requests" v-model.number="requestCount" hint="Using random files"
									persistent-hint v-mask="'####'" v-validate="'required'" data-vv-name="number" :error-messages="errors.collect('number')" required></v-text-field>
							</v-col>
							<v-col class="pr-3" cols="5" >
								<v-radio-group v-model="timing" row>
									<template v-slot:label>
										<div>Timing</div>
									</template>
									<v-radio label="Immediate" value="immediate"></v-radio>
									<v-radio label="Spread" value="spread"></v-radio>
								</v-radio-group>
							</v-col>
							<v-col cols="2">
								<v-text-field v-if="timing === 'spread'" key="tm2" label="Spread Minutes" v-model.number="timeSpanMin"
									persistent-hint v-mask="'###'" v-validate="'required'" data-vv-name="TimeSpan" :error-messages="errors.collect('TimeSpan')" required></v-text-field>
							</v-col>
						</v-row>
						<v-row v-else>
							<v-col>
								<v-text-field key="ts" label="URL" v-model="analysis.url" v-validate="{required: true, url: {require_protocol: true }}" data-vv-name="URL" :error-messages="errors.collect('URL')" required clearable @click:clear="clearUrl"></v-text-field>
							</v-col>
						</v-row>
					</v-col>
					<v-col cols="12" v-show="!analysis.url && mode === 'single'">
						<p class="caption info--text my-2">Enter a URL or select a test file below</p>
						<test-files @selected="sampleSelected" @loaded="samplesLoaded"></test-files>
					</v-col>
				</v-row>
				<v-row  >
					<v-col class="pt-3" cols="12" >
						<v-btn @click="startAnalysis" color="primary" :disabled="errors.any()" class="mr-3">Submit</v-btn>
						<v-btn @click="reset">Reset</v-btn>
					</v-col>
				</v-row>
			</v-col>
		</v-row>
		<v-row   v-if="currentState !== state.Configuring">
			<v-col class="mb-3" cols="12" >
				<v-alert
					:value="currentState === state.Generating"
					type="info"
				>
					Tests are running.
				</v-alert>
				<v-alert
					:value="currentState === state.Complete"
					type="success"
				>
					Generation is complete.
				</v-alert>
				<v-alert
					:value="currentState === state.Cancelled"
					type="warning"
				>
					Generation cancelled.
				</v-alert>
			</v-col>
			<v-col class="mb-3" cols="12" >
				<v-btn v-if="currentState !== state.Complete" @click="cancelClick" color="error" :disabled="currentState === state.Cancelled">Cancel Generation</v-btn>
				<v-btn @click="reset" :disabled="currentState === state.Generating">Reset</v-btn>
			</v-col>

			<v-col cols="2">
				<v-progress-circular
					:size="100"
					:width="15"
					:value="progress"
					color="teal"
				>
					{{ requestSuccess + requestFail }}
				</v-progress-circular>
			</v-col>
			<v-col cols="10">
				<v-row  class="subtitle-1">
					<v-col class="shrink pr-3" >Pending: <b class="info--text">{{requestCount - requestSuccess - requestFail}}</b></v-col>
					<v-col class="shrink pr-3" >Success: <b class="success--text">{{requestSuccess}}</b></v-col>
					<v-col class="shrink" >Errors: <b class="error--text">{{requestFail}}</b></v-col>
					<v-spacer></v-spacer>
				</v-row>
			</v-col>
			<v-col class="mt-5" cols="12" >
				<table class="test-transactions-table tight v-table theme--light" v-if="sentRequests.length > 0">
					<thead>
						<tr>
							<th></th>
							<th>Wait</th>
							<th>Tracking #</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in sentRequests" :key="item.trackingNumber">
							<td style="width:75px">
								<v-icon color="success" v-if="item.status === 'Complete'">fas fa-check-circle</v-icon>
								<v-icon color="error" v-if="item.status === 'Error'">fas fa-times-circle</v-icon>
								<v-icon color="info" v-if="item.status === 'Pending'">fas fa-clock</v-icon>
								<v-icon color="warning" v-if="item.status === 'Cancelled'">fas fa-ban</v-icon>
							</td>
							<td style="width:100px">{{item.interval}} sec</td>
							<td>
								<p v-if="item.error" class="warning--text">{{item.error}}</p>
								<tracking-chip v-else :trackingNumber="item.trackingNumber" type="Analysis" :static="true"></tracking-chip>
							</td>
						</tr>
					</tbody>
				</table>
			</v-col>
		</v-row>
	</div>
</template>

<script>
// import _ from "lodash";
// import { requestTypes } from "../enums";
import { mask } from "vue-the-mask";
import api from "../api";
import analysisApi from "../analysisApi";
import testFiles from "./test-files.vue";
import trackingChip from "../components/tracking-chip.vue";

// later?: filter samples grid by name
// TODO, add a link to watch system status at this time

const state = {
	Configuring: "Configuring",
	Generating: "Generating",
	Cancelled: "Cancelled",
	Failed: "Failed",
	Complete: "Complete",
};

export default {
	components: {
		testFiles,
		trackingChip,
	},
	directives: { mask },
	data() {
		return {
			apiPing: null,
			apiOK: false,
			checkingApi: true,

			// token: null,
			// analysis: {
			// 	format: null,
			// 	type: null,
			// 	url: null,
			// },
			// mode: "multiple",
			// timing: "spread",
			token: "AiZzPZ6V3hLe35l5ixVYJWmp9uOzrcwM4BoQ1KkidroAamMjOm0aexNy3Fw8JIxN",
			analysis: {
				format: "LifeSignals",
				type: "Holter",
				url: null,
			},
			mode: "multiple",
			timing: "spread",
			requestCount: 10,
			timeSpanMin: 5,

			currentState: state.Configuring,
			selectedSample: null,
			tokenTest: null,
			tokenTestComplete: false,
			tokenTestError: null,
			submitError: false,
			samples: [],
			availableSamples: [],
			sentRequests: [],
			state,
			timer: null,
		};
	},
	created() {
		analysisApi.serverStatus().then((r) => {
			this.apiPing = r.data;
			this.apiOK = true;
			this.checkingApi = false;

			return this.refreshStatus();
		}, (e) => {
			this.apiOK = false;
			this.checkingApi = false;
			this.showError(e);
		}).then(() => {
			if (this.sentRequests.filter(s => s.status === "Pending").length > 0) {
				this.requestCount = this.sentRequests.length;
				this.currentState = state.Generating;
				this.timer = setInterval(this.statusCheck, 5000);
			} else {
				this.sentRequests = [];
			}
		});
	},
	beforeDestroy() {
		if (this.timer) { clearInterval(this.timer); }
	},
	watch: {
		token(v) {
			analysisApi.setToken(v);

			analysisApi.validateToken().then(() => {
				this.tokenTest = true;
				this.tokenTestComplete = true;
				this.tokenTestError = null;
			}, (e) => {
				this.tokenTest = false;
				this.tokenTestComplete = true;
				this.tokenTestError = e;
				this.showError(e);
			});
		},
		mode() {
			this.$nextTick(() => {
				this.requestCount = this.mode === "single" ? 1 : 10;
				this.analysis.url = null;
			});
		},
	},
	computed: {
		tokenIcon() {
			if (!this.tokenTestComplete) {
				return null;
			}

			return this.tokenTest ? "far fa-check-circle" : "far fa-exclamation-circle";
		},
		tokenClass() {
			if (!this.tokenTestComplete) {
				return null;
			}

			return this.tokenTest ? "token-ok" : "token-error";
		},
		progress() {
			return 100 * (this.requestSuccess + this.requestFail) / this.requestCount;
		},
		requestSuccess() {
			return this.sentRequests.filter(r => r.status === "Complete").length;
		},
		requestFail() {
			return this.sentRequests.filter(r => r.status === "Error").length;
		},
	},
	methods: {
		samplesLoaded(samples) {
			this.samples = samples;
		},
		clearUrl() {
			this.selectedSample = null;
			this.analysis.url = null;
		},
		sampleSelected(item) {
			this.selectedSample = item;
			this.analysis.url = item.zip.url;
		},
		startAnalysis() {
			return this.$validator.validateAll()
				.then((valid) => {
					if (!valid) {
						return Promise.resolve();
					}

					this.currentState = state.Generating;
					this.submitError = null;

					const options = {
						applicationToken: this.token,
						format: this.analysis.format,
						type: this.analysis.type,
						url: this.analysis.url,
						requestCount: this.requestCount,
						minutes: this.timeSpanMin,
					};

					if (this.mode === "single") {
						options.requestCount = 1;
						options.minutes = 0;
					}
					if (this.timing === "immediate") {
						options.minutes = 0;
					}

					return api.testGenerate(options).then((r) => {
						this.sentRequests = r.data;

						this.timer = setInterval(this.statusCheck, 5000);
					}).catch((e) => {
						this.showError(e);
					});
				});
		},
		refreshStatus() {
			return api.testGenerateStatus().then((r) => {
				this.sentRequests = r.data;
			});
		},
		statusCheck() {
			this.refreshStatus().then(() => {
				if (this.sentRequests.filter(s => s.status === "Pending").length === 0) {
					clearInterval(this.timer);
					if (this.currentState !== state.Cancelled) {
						this.currentState = state.Complete;
					}
				}
			});
		},
		cancelClick() {
			this.currentState = state.Cancelled;
			api.testGenerateCancel();
		},
		reset() {
			analysisApi.setToken(null);
			this.$router.go();
		},
	},
};
</script>
